import { Button } from "@/components/Button";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";
import Modal from "./Modal";
import { H1, P } from "@/components/Typography";
import stressedPersonUsingComputerAtDesk from "@/assets/images/collect/stressed-person-using-computer-at-desk.png";
import { useCancelSubscription } from "@/requests/hooks/user";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

const styles = tv({
  slots: {
    base: "flex flex-row justify-center text-center",
    rowContent: "flex flex-col",
    iconContainer: "mb-10 flex flex-row justify-center",
    icon: "h-full w-full md:w-2/3",
    buttonContainer: "mt-20 flex items-center justify-center",
  },
});

interface IProps {
  toggleIsOpen: () => void;
  refetchBilling: () => void;
  isOpen: boolean;
  billing?: IGetBillingRS;
}

const CancelSubscriptionModal = ({
  toggleIsOpen,
  refetchBilling,
  isOpen,
  billing,
}: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { base, rowContent, iconContainer, icon, buttonContainer } = styles();

  const {
    isSuccess: isCancelSubscriptionSuccess,
    isPending: isCancelSubscriptionPending,
    mutate: cancelSubscription,
  } = useCancelSubscription();

  useEffect(() => {
    if (isCancelSubscriptionSuccess) {
      toggleIsOpen();
      toast.success(t("settings.billing.cta.success"));
      queryClient.removeQueries({ queryKey: ["billing"] });
      refetchBilling();
    }
  }, [isCancelSubscriptionSuccess]);

  const onClickCancelSubscription = () => {
    const { subscription: currentSubscription, upcomingSubscription } =
      billing as IGetBillingRS;
    const subscription = currentSubscription || upcomingSubscription;
    const id = subscription?.id;
    const version = subscription?.version;

    if (!!id && !!version)
      cancelSubscription({
        id,
        version,
      });
  };

  const renderImageSection = () => (
    <div className={iconContainer()}>
      <img
        src={stressedPersonUsingComputerAtDesk}
        className={icon()}
        alt="Image of a stressed person using a computer at a desk"
      />
    </div>
  );

  const renderTextBodySection = () => (
    <div className="max-sm:p-10">
      <div className="mb-10">
        <H1 className="font-bold text-secondary">
          {t("settings.billing.modal.title")}
        </H1>
      </div>
      <div className="mb-10">
        <P>{t("settings.billing.modal.descriptionUpper")}</P>
      </div>
      <P>
        {t("settings.billing.modal.descriptionMiddle")}
        {t("settings.billing.modal.descriptionLower")}
      </P>
    </div>
  );

  const renderButton = () => {
    const items = billing?.subscription?.items;
    const collectSubscription = items?.find(sub => sub.product === "COLLECT");

    return (
      <div className={buttonContainer()}>
        <Button
          as="button"
          type="button"
          variant="primary"
          trackingId={{
            eventName: "control-panel.billing.confirm-cancel-subscription",
            additionalProperties: {
              subscription_name: collectSubscription?.tier?.toLowerCase(),
            },
          }}
          onClick={onClickCancelSubscription}
          loading={isCancelSubscriptionPending}>
          {t("settings.billing.modal.cancelSubscriptionCta")}
        </Button>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggleIsOpen={toggleIsOpen}>
      <div className={base()}>
        <div className={rowContent()}>
          {renderImageSection()}
          {renderTextBodySection()}
          {renderButton()}
        </div>
      </div>
    </Modal>
  );
};

export default CancelSubscriptionModal;
